import React from "react";
import classNames from "classnames";
import { Avatar } from "ui/components/avatar";
import { RenderAvatar } from "components/render-avatar";

const CrownIcon = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="#F59E0B"
        xmlns="http://www.w3.org/2000/svg"
        className="drop-shadow-md"
    >
        <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5ZM19 19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V17H19V19Z" />
    </svg>
);

const MAX_VISIBLE_AVATARS = 6;

function formatTiedNames(employees) {
    if (!employees?.length) return "";
    if (employees.length === 1) {
        return employees[0]?.first_name ?? "";
    }
    const names = employees.map((e) => e?.first_name).filter(Boolean);
    if (names.length <= 2) {
        return names.join(" & ");
    }
    const shown = names.slice(0, 2).join(", ");
    const rest = names.length - 2;
    return `${shown} +${rest}`;
}

function TiedAvatarStack({ employees, isCenter, rank }) {
    const list = employees ?? [];
    const showCrown = rank === 1;

    if (list.length === 0) {
        return null;
    }

    const visible = list.slice(0, MAX_VISIBLE_AVATARS);
    const overflow = list.length - visible.length;

    const single = list.length === 1;
    const avatarSize = single
        ? classNames({
              "size-20 sm:size-24": isCenter,
              "size-14 sm:size-16": !isCenter,
          })
        : classNames({
              "size-12 sm:size-14": isCenter,
              "size-10 sm:size-12": !isCenter,
          });

    return (
        <div
            className={classNames(
                "flex flex-col items-center relative transition-transform hover:scale-[1.02] duration-300",
                { "mb-6": isCenter }
            )}
        >
            

            <div
                className={classNames(
                    "flex flex-row justify-center items-center",
                    single ? "" : "-space-x-2 sm:-space-x-2.5"
                )}
                title={list
                    .map((e) => [e?.first_name, e?.last_name].filter(Boolean).join(" "))
                    .filter(Boolean)
                    .join(", ")}
            >
                {visible.map((employee, i) => (
                    <Avatar
                        key={employee?.id ?? `${i}-${employee?.first_name}`}
                        className={classNames(
                            "border-4 border-white shadow-lg ring-2 ring-white/80",
                            avatarSize
                        )}
                        style={
                            !single
                                ? { zIndex: visible.length - i }
                                : undefined
                        }
                    >
                        <RenderAvatar user={employee} />
                    </Avatar>
                ))}
                {overflow > 0 && (
                    <div
                        className={classNames(
                            "flex items-center justify-center rounded-full border-4 border-white bg-zinc-700 text-white font-bold shadow-lg ring-2 ring-white/80",
                            single ? "size-20 sm:size-24 text-sm" : "size-10 sm:size-12 text-xs"
                        )}
                        style={{ zIndex: 0 }}
                        title={`${overflow} more`}
                    >
                        +{overflow}
                    </div>
                )}
            </div>

            <span
                className={classNames(
                    "text-gray-800 font-semibold mt-2 text-center px-1 max-w-[11rem] sm:max-w-[14rem] drop-shadow-sm",
                    single ? "text-sm sm:text-lg whitespace-nowrap" : "text-xs sm:text-sm leading-snug line-clamp-2"
                )}
            >
                {formatTiedNames(list)}
            </span>
            {list.length > 2 && (
                <span className="text-[10px] sm:text-xs text-zinc-500 mt-0.5 font-medium">
                    {list.length} tied on this rank
                </span>
            )}
        </div>
    );
}

const PodiumItem = ({ item, rank, heightClass, isCenter = false }) => {
    const employees = item?.employees ?? [];
    const displayRank = item?.rank ?? rank;

    if (!item || employees.length === 0) {
        return <div className={`w-24 sm:w-36 ${heightClass}`} />;
    }

    return (
        <div className="flex flex-col items-center z-10 min-w-0 flex-1 max-w-[11rem] sm:max-w-[14rem]">
            <TiedAvatarStack employees={employees} isCenter={isCenter} rank={rank} />

            <div
                className={classNames(
                    "w-full min-w-[5.5rem] sm:min-w-[7rem] flex flex-col items-center justify-start pt-4 rounded-t-lg bg-gradient-to-br from-[#ff4d4d] to-[#990000] text-white shadow-xl transition-all hover:brightness-110",
                    heightClass
                )}
            >
                <span className="text-5xl sm:text-7xl font-black drop-shadow-sm">{displayRank}</span>
                <span className="text-xs sm:text-sm mt-1 font-medium opacity-90 px-1 text-center">
                    {Number(item.total_average_performance || 0).toFixed(2)} avg
                </span>
            </div>
        </div>
    );
};

export function Podium({ items }) {
    const first = items?.[0];
    const second = items?.[1];
    const third = items?.[2];

    return (
        <div className="w-full mb-8 pt-12">
            <div className="flex items-end justify-center gap-1.5 sm:gap-4 md:gap-6 w-full min-h-[400px] px-1">
                <PodiumItem item={second} rank={2} heightClass="h-40 sm:h-52" />
                <PodiumItem item={first} rank={1} heightClass="h-52 sm:h-64" isCenter />
                <PodiumItem item={third} rank={3} heightClass="h-28 sm:h-40" />
            </div>
        </div>
    );
}
